<template>
  <div class="partnerlist">
    <div class="d-flex mb-4">
      <DateFilter @change-date="setDateFilter" @change="applyDateFilter" :value="dateFilter" />
    </div>
    <v-data-table
      :mobile-breakpoint="100"
      class="partnerlist-table"
      :headers="headers"
      :items="payouts"
      item-key="id"
      :server-items-length="total"
      :options.sync="options"
      :footer-props="footerProps"
      :no-data-text="noDataText"
    >
      <template #top>
        <v-text-field
          dense
          single-line
          hide-details
          outlined
          v-model="search_query"
          label="Введите ИНН, ОГРН или наименование"
          class="mb-4"
          clearable
        ></v-text-field>
      </template>

      <template #[`item.dadd`]="{ value }">
        <span>
          <span>{{ new Date(value).toLocaleDateString() }}</span
          ><br />
          <span class="font font--label-light font--color_light">{{ new Date(value).toLocaleTimeString() }}</span>
        </span>
      </template>

      <template #[`item.client_name`]="{ value, item }">
        <span>
          <span>{{ value }}</span>
          <br />
          <span class="font font--label-light font--color_light" v-if="item.inn"> ИНН: {{ item.inn }} </span>
        </span>
      </template>

      <template #[`item.sum`]="{ value }">
        <span class="span">{{ value }} &#8381;</span>
      </template>

      <template #[`item.transaction_status`]="{ value }">
        <PayoutStatusChip v-if="value" :value="value" />
      </template>

      <template #[`item.requisites`]="{ value }">
        <span class="font font--label-light font--color_light">Счёт: {{ value.acc }}</span>
        <br />
        <span class="font font--label-light font--color_light">БИК: {{ value.bic }}</span>
        <br />
        <span class="font font--label-light font--color_light">ФИО: {{ value.client_name }}</span>
        <br />
        <span class="font font--label-light font--color_light">ИНН: {{ value.inn }}</span>
        <br />
        <span v-if="value.ogrn" class="font font--label-light font--color_light">ОГРН: {{ value.ogrn }}</span>
      </template>

      <template #[`item.user_id`]="{ item }">
        <PayoutsListActions :item="item" />
      </template>
    </v-data-table>
    <PayoutsListModal />
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import DateFilter from "@/components/common/DateFilter";
import PayoutsListActions from "@/components/payouts/PayoutsListActions";
import PayoutsListModal from "@/components/payouts/PayoutsListModal";
import PayoutStatusChip from "@/components/payouts/PayoutStatusChip";

import { formatPhone } from "@/utils/helpers";

export default {
  name: "PayoutsList",
  components: {
    DateFilter,
    PayoutsListActions,
    PayoutsListModal,
    PayoutStatusChip,
  },

  data() {
    return {
      footerProps: {
        itemsPerPageText: "Пользователей на странице:",
        itemsPerPageAllText: "Все",
        pageText: "{0}-{1} из {2}",
        itemsPerPageOptions: [5, 10, 15, 30, 50],
      },
      noDataText: "Нет пользователей",
    };
  },

  computed: {
    ...mapState("payouts", ["total", "payouts"]),
    ...mapGetters("payouts", ["dateFilter"]),

    isOpen() {
      return this.open;
    },

    search_query: {
      get() {
        return this.$store.state.payouts.search_query;
      },
      set(value) {
        this.setSearchQuery(value);
      },
    },

    options: {
      get() {
        return {
          page: this.$store.state.payouts.page,
          itemsPerPage: this.$store.state.payouts.pageSize,
        };
      },
      set({ page, itemsPerPage }) {
        this.setPage(page);
        this.setPageSize(itemsPerPage);
      },
    },

    headers() {
      return [
        {
          text: "Дата",
          value: "dadd",
          sortable: false,
        },
        { text: "Партнер", value: "client_name", sortable: false },
        { text: "Сумма", value: "sum", sortable: false },
        { text: "Статус вывода", value: "transaction_status", sortable: false },
        { text: "Реквизиты", value: "requisites", sortable: false, align: "left" },
        { value: "user_id", sortable: false, align: "right" },
      ];
    },
  },

  watch: {
    search_query() {
      this.debouncedSearch();
    },

    options: {
      handler() {
        this.getAllPayouts();
      },
      deep: true,
    },
  },

  methods: {
    ...mapMutations("payouts", ["setDateFilter", "setPageSize", "setPage", "setSearchQuery", "setShowModal"]),
    ...mapActions("payouts", ["getAllPayouts"]),
    openPayoutModal() {
      this.setShowModal();
    },
    formatPhone(value) {
      return formatPhone(value);
    },

    applyDateFilter({ start, end }) {
      this.setDateFilter({ start, end });
      this.options = { page: 1, itemsPerPage: this.options.itemsPerPage };
      this.getAllPayouts();
    },

    applyUserInputFilter() {
      this.options = { page: 1, itemsPerPage: this.options.itemsPerPage };
      this.getAllPayouts();
    },
  },

  created() {
    this.debouncedSearch = debounce(this.applyUserInputFilter, 500);
  },
};
</script>

<style lang="scss">
.theme--light.v-data-table.partnerlist-table > .v-data-table__wrapper > table > thead > tr > th {
  color: #777c87;
}

.v-data-table.partnerlist-table > .v-data-table__wrapper > table > thead > tr > th {
  text-transform: uppercase;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: 0.1em;
  font-weight: 400;
  height: auto;
  padding: 12px 16px;
}

.v-data-table.partnerlist-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding: 16px;
}

.v-data-table-header {
  background-color: #f5f5f6;
}

.slide-leave-active,
.slide-enter-active {
  transition: 0.5s;
}
.slide-enter {
  transform: translate(100%, 0);
}
.slide-leave-to {
  transform: translate(100%, 0);
}
.span {
  white-space: nowrap;
}
</style>
